import { supabase } from 'supabaseClient';
import { createTeamsAPI } from './teams';
import { createChallengesAPI } from './challenges';
import { createBudgetsAPI } from './budgets';
import { createCalendarAPI } from './calendar';
import { createProfilesAPI } from './profiles';
import { createAnnouncementsAPI } from './announcements';
import { createOrganizationsAPI } from './organizations';

const debug = () => {};
// const debug = console.warn.bind(console);


export const API_ERROR_TYPES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION: 'VALIDATION',
  SERVER_ERROR: 'SERVER_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED'
};

export class APIError extends Error {
  constructor(type, message, status = null, originalError = null) {
    super(message);
    this.type = type;
    this.status = status;
    this.originalError = originalError;
  }
}

const BASE_URL = process.env.REACT_APP_ENV === 'development'
  ? process.env.REACT_APP_BACKEND_URL_LOCAL
  : process.env.REACT_APP_BACKEND_URL;

export const createAPIClient = async () => {
  debug('Creating API client with BASE_URL:', BASE_URL);
  
  // Get current session token
  let { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError || !session) {
    debug('No valid session found, attempting refresh...', { sessionError });
    // Try to refresh the session
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) {
      console.error('Session refresh failed:', refreshError);
      throw new APIError(
        API_ERROR_TYPES.UNAUTHORIZED,
        'Session expired. Please log in again.',
        401,
        refreshError
      );
    }
    session = refreshData.session;
    debug('Session refreshed successfully');
  }

  const request = async (endpoint, options = {}, retryCount = 0) => {
    const url = `${BASE_URL}${endpoint}`;
    debug(`Making API request to ${endpoint}`, {
      method: options.method,
      retryCount,
      hasToken: !!session?.access_token
    });
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
          ...options.headers,
        },
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        const error = data;
        console.error(`API request failed for ${endpoint}:`, {
          status: response.status,
          error: error,
          retryCount
        });
        
        // Handle JWT expiration
        if ((error?.originalError?.code === 'PGRST301' || 
             error?.message === 'JWT expired') && 
            retryCount < 2) {
          debug('Token expired, attempting refresh...');
          
          // Try to refresh the token
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (!refreshError && refreshData.session) {
            debug('Token refreshed successfully, retrying request...');
            session = refreshData.session;
            // Retry the request with new token
            return request(endpoint, options, retryCount + 1);
          }
          
          throw new APIError(
            API_ERROR_TYPES.TOKEN_EXPIRED,
            'Session expired and refresh failed. Please log in again.',
            401,
            refreshError
          );
        }
        
        // Handle other error types
        switch (response.status) {
          case 401:
            throw new APIError(
              API_ERROR_TYPES.UNAUTHORIZED,
              error.message || 'Please log in again',
              response.status
            );
          case 403:
            throw new APIError(
              API_ERROR_TYPES.FORBIDDEN,
              error.message || 'You don\'t have permission for this action',
              response.status
            );
          case 404:
            if (error.code === 'NO_TEAM_FOUND') {
              return [];
            }
            throw new APIError(
              API_ERROR_TYPES.NOT_FOUND,
              error.message || 'Resource not found',
              response.status
            );
          case 422:
            throw new APIError(
              API_ERROR_TYPES.VALIDATION,
              error.message || 'Invalid data provided',
              response.status
            );
          default:
            throw new APIError(
              API_ERROR_TYPES.SERVER_ERROR,
              error.message || 'An unexpected error occurred',
              response.status
            );
        }
      }
      
      debug(`API request to ${endpoint} succeeded`, {
        dataReceived: !!data,
        isArray: Array.isArray(data),
        length: Array.isArray(data) ? data.length : undefined
      });
      
      return data;
    } catch (error) {
      console.error(`API request to ${endpoint} failed:`, {
        name: error.name,
        message: error.message,
        type: error.type,
        status: error.status,
        stack: error.stack
      });
      
      if (error instanceof APIError) {
        throw error;
      }
      
      if (!navigator.onLine) {
        throw new APIError(
          API_ERROR_TYPES.NETWORK_ERROR,
          'Please check your internet connection',
          null,
          error
        );
      }
      
      throw new APIError(
        API_ERROR_TYPES.SERVER_ERROR,
        'Failed to connect to server',
        null,
        error
      );
    }
  };

  const client = {
    challenges: createChallengesAPI(request),
    teams: createTeamsAPI(request),
    budgets: createBudgetsAPI(request),
    calendar: createCalendarAPI(request),
    profiles: createProfilesAPI(request),
    announcements: createAnnouncementsAPI(request),
    organizations: createOrganizationsAPI(request)
  };

  debug('API client created successfully', {
    availableAPIs: Object.keys(client)
  });

  return client;
}; 